<template>
    <div id="user-management-view">
        <v-data-table
            :headers="headers"
            :footer-props="footerProps"
            :items-per-page="footerProps.itemsPerPageOptions[2]"
            :items="users"
            locale="en-UK"
            :search="search"
            :loading="loading"
            class="elevation-1"
            loading-text="Loading... Please wait">
            <template v-slot:top>

                <v-toolbar flat>
                    <v-toolbar-title>Users {{ users.length ? `(${users.length})` : '' }}</v-toolbar-title>
                    <v-divider class="mx-4" inset vertical />
                    <v-spacer />

                    <v-dialog
                        v-if="users.length"
                        v-model="editUser"
                        persistent
                        max-width="600px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn color="primary" dark class="mb-2" v-bind="attrs" v-on="on">New User</v-btn>
                        </template>
                        <v-card id="user-management-view-edit">
                            <v-card-title>
                                <v-row>
                                    <v-col cols="6">
                                        <span class="pt-2 text-h5">{{ formTitle }}</span>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-switch
                                            v-model="userProfileFirestore.display"
                                            :disabled="editedIndex < 0"
                                            label="Display"
                                            color="yellow"
                                            class="ma-0 float-end"
                                            hide-details
                                            @change="visiblePublicly(editedItem.uid)">
                                        </v-switch>
                                    </v-col>
                                </v-row>
                            </v-card-title>
                            <v-card-text>
                                <v-form id="user-dialog" ref="dialogForm" v-model="valid" lazy-validation>
                                    <v-container class="pa-1">
                                        <v-row>
                                            <v-col cols="12" md="4" align="center" align-self="center">
                                                <div class="photo-wrapper">
                                                    <v-btn fab small class="remove-photo" elevation="2" v-show="editedItem.photoURL">
                                                        <v-icon @click="editedItem.photoURL = null">mdi-close</v-icon>
                                                    </v-btn>
                                                    <input
                                                        type="file"
                                                        class="d-none"
                                                        ref="uploader"
                                                        accept="image/*"
                                                        @change="selectFile">
                                                    <v-img
                                                        :key="editedItem.photoURL"
                                                        :src="editedItem.photoURL"
                                                        :lazy-src="editedItem.photoURL"
                                                        height="120"
                                                        width="120"
                                                        class="fill-height profile-pic rounded-circle"
                                                        @click="$refs.uploader.click()">
                                                        <template v-slot:placeholder>
                                                            <v-avatar class="fill-height" color="primary" width="120" height="120" rounded>
                                                                <v-icon color="white" large v-if="!loadingImage">mdi-upload</v-icon>
                                                                <v-progress-circular indeterminate color="white" v-else />
                                                            </v-avatar>
                                                        </template>
                                                    </v-img>
                                                </div>
                                            </v-col>
                                            <v-col cols="12" md="8">
                                                <v-text-field
                                                    v-model="editedItem.displayName"
                                                    :rules="nameRules"
                                                    label="Name"
                                                    required>
                                                </v-text-field>
                                                <v-text-field
                                                    v-model="editedItem.email"
                                                    :rules="emailRules"
                                                    label="E-mail"
                                                    required>
                                                </v-text-field>
                                                <v-text-field
                                                    v-if="editedIndex < 0"
                                                    v-model="editedItem.password"
                                                    :rules="passwordRules"
                                                    label="Password"
                                                    required>
                                                </v-text-field>
                                                <v-select
                                                    v-if="editedIndex >= 0"
                                                    v-model="editedItem.role"
                                                    :items="roles"
                                                    label="Role"
                                                    class="mb-2"
                                                    hide-details>
                                                </v-select>
                                            </v-col>
                                            <v-col cols="12">
                                                <v-text-field
                                                    v-model="userProfileFirestore.title"
                                                    elevation="0"
                                                    class="pt-0 pb-4"
                                                    placeholder="Job Title"
                                                    prepend-inner-icon="mdi-card-account-details"
                                                    hide-details>
                                                </v-text-field>
                                                <v-text-field
                                                    v-model="userProfileFirestore.linkedin"
                                                    elevation="0"
                                                    class="pb-4"
                                                    placeholder="https://www.linkedin.com/in/yourname"
                                                    prepend-inner-icon="mdi-linkedin">
                                                </v-text-field>
                                                <v-textarea
                                                    v-model="userProfileFirestore.bio"
                                                    label="About me"
                                                    name="about me"
                                                    rows="3"
                                                    filled
                                                    outlined
                                                    auto-grow
                                                    hide-details>
                                                </v-textarea>
                                            </v-col>
                                            <v-col cols="12">
                                                <v-card-actions class="pa-0 align-end">
                                                    <v-spacer></v-spacer>
                                                    <v-btn plain @click="close">Close</v-btn>
                                                    <v-btn color="primary" @click="save">Save</v-btn>
                                                </v-card-actions>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-form>
                            </v-card-text>
                        </v-card>
                    </v-dialog>

                    <v-btn text icon class="mb-2 ml-2" @click="initialize">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </v-toolbar>
            </template>

            <template v-slot:[`body.prepend`]="{ headers }">
                <tr class="mx-0 px-0">
                    <td :colspan="headers.length" class="mx-0 px-0">
                        <v-text-field
                            v-model="search"
                            prepend-inner-icon="mdi-magnify"
                            label="Search"
                            single-line
                            hide-details
                            filled
                            flat
                            clearable
                            class="px-0 mx-0 rounded-0">
                        </v-text-field>
                    </td>
                </tr>
            </template>

            <template v-slot:[`item.customClaims`]="{ value }">
                <span class="text-overline primary--text" v-if="value.admin">
                    <v-icon color="primary">mdi-account-tie</v-icon> {{ Object.keys(value)[0] }}
                </span>
                <span class="text-overline" v-else>
                    <v-icon>mdi-account</v-icon> {{ Object.keys(value)[0] }}
                </span>
            </template>

            <template v-slot:[`item.emailVerified`]="{ value }">
                <v-icon :color="value ? 'success': 'error'">{{ value ? 'mdi-check' : 'mdi-close '}}</v-icon>
            </template>

            <template v-slot:[`item.disabled`]="{ item, value }">
                <v-switch
                    :input-value="!value"
                    :disabled="loading || isCurrentUser(item) || item.email === 'otisak.pravilaw@gmail.com'"
                    color="primary"
                    hide-details
                    class="ma-0 pa-0"
                    @change="disableUser(item, value)">
                </v-switch>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
                <v-btn :disabled="loading" icon text class="mr-2" @click="editItem(item)">
                    <v-icon medium>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                    icon text
                    :disabled="loading || isCurrentUser(item) || item.email === 'otisak.pravilaw@gmail.com'"
                    @click="deleteItemDialog(item)">
                    <v-icon medium color="error">mdi-delete</v-icon>
                </v-btn>
            </template>

            <template v-slot:no-data>
                <v-btn color="primary" class="my-2" @click="initialize">Refresh</v-btn>
            </template>

        </v-data-table>

        <SimpleDialog
            :visible="deleteDialog"
            title="Remove User"
            :message="`You're about to remove ${itemToDelete.displayName} from users.`"
            yes="Remove"
            no="Cancel"
            @on-yes="deleteItem(itemToDelete)"
            @on-no="deleteDialog = false">
        </SimpleDialog>

        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
            <span>{{ snackText }}</span>
            <template v-slot:action="{ attrs }">
                <v-icon v-bind="attrs" text @click="snack = false">mdi-close</v-icon>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/storage';
import SimpleDialog from "../../components/SimpleDialog";
import {showSnack} from "@/components/mixins/showSnack";

export default {
    name: 'UserManagement',

    components: {
        SimpleDialog
    },

    mixins: [showSnack],

    data() {
        return {
            search: '',
            loading: true,
            loadingImage: false,
            editedIndex: -1,
            createItem: {
                displayName: null,
                email: '',
                password: '',
                photoURL: null
            },
            editedItem: {},
            userProfileFirestore: {
                displayName: '',
                photoURL: '',
                display: false,
                bio: '',
                linkedin: '',
                title: ''
            },
            valid: true,
            nameRules: [
                v => !!v || 'Name is required'
            ],
            emailRules: [
                v => !!v || 'E-mail is required',
                v => /.+@.+\..+/.test(v) || 'E-mail must be valid'
            ],
            passwordRules: [
                v => this.editedIndex < 0 ? !!v || 'Password is required' : true,
                v => this.editedIndex < 0 ? (v && v.length > 6) || 'Must be longer than 6 characters' : true
            ],
            roles: [
                'User',
                'Admin'
            ],

            editUser: false,
            deleteDialog: false,
            itemToDelete: {},
            headers: [
                {
                    text: 'Full Name',
                    align: 'start',
                    sortable: true,
                    value: 'displayName',
                    cellClass: 'body-2 font-weight-medium'
                },
                {
                    text: 'Role',
                    align: 'start',
                    sortable: false,
                    value: 'customClaims'
                },
                {
                    text: 'E-mail',
                    align: 'start',
                    sortable: false,
                    value: 'email'
                },
                {
                    text: 'E-mail Verified',
                    align: 'center',
                    sortable: false,
                    value: 'emailVerified'
                },
                {
                    text: 'Active',
                    align: 'left',
                    sortable: false,
                    value: 'disabled'
                },
                {
                    text: 'Actions',
                    value: 'actions',
                    sortable: false,
                    align: 'right'
                }
            ],
            footerProps: {
                itemsPerPageOptions: [5, 10, -1],
                itemsPerPageText: 'Users per page',
                showCurrentPage: true
            },
        };
    },

    watch: {
        editUser(val) {
            val || this.close();
        }
    },

    created() {
        this.editedItem = Object.assign({}, this.createItem);
        this.initialize();
    },

    beforeRouteLeave(to, from, next) {
        if (this.editUser) {
            this.close();
            next(false);
        } else {
            next();
        }
    },

    computed: {
        ...mapGetters({
            user: 'auth/user',
            users: 'users/getUsers'
        }),
        formTitle() {
            return this.editedIndex === -1 ? 'Add New User' : 'Edit User';
        }
    },

    methods: {
        ...mapActions({
            loadUsers: 'users/loadUsers',
            addUser: 'users/addUser',
            updateUser: 'users/updateUser',
            updateUserRole: 'users/updateUserRole',
            removeUser: 'users/removeUser'
        }),

        async initialize() {
            this.loading = true;
            try {
                await this.loadUsers();
            } catch (e) {
                console.error(e);
            }
            this.loading = false;
        },
        getUserProfileFirestore(item) {
            const { displayName, photoURL } = item;
            const vm = this;
            firebase.firestore().collection(process.env.VUE_APP_COLLECTION_USERS).doc(item.uid).get()
                .then(doc => {
                    if (doc.exists) {
                        vm.userProfileFirestore = doc.data();
                    } else {
                        vm.userProfileFirestore = {
                            displayName,
                            photoURL,
                            display: false,
                            bio: '',
                            linkedin: '',
                            title: ''
                        }
                    }
                });
        },
        async saveUserProfileFirestore(id, payload) {
            await firebase.firestore().collection(process.env.VUE_APP_COLLECTION_USERS)
                .doc(id)
                .set(payload);
        },
        async deleteUserProfileFirestore(id) {
            await firebase.firestore().collection(process.env.VUE_APP_COLLECTION_USERS).doc(id).delete();
        },
        editItem(item) {
            this.getUserProfileFirestore(item);
            this.editedIndex = this.users.indexOf(item);
            this.editedItem = Object.assign({}, item);
            this.editUser = true;
        },
        close() {
            this.editUser = false;
            this.$nextTick(() => {
                this.editedItem = Object.assign({}, this.createItem);
                this.editedIndex = -1;
                this.$refs.dialogForm.reset();
            });
        },
        deleteItemDialog(item) {
            this.deleteDialog = true;
            this.itemToDelete = Object.assign({}, item);
        },
        isCurrentUser(item) {
            return this.user.data.uid === item.uid;
        },
        async deleteItem(item) {
            if (this.isCurrentUser(item)) {
                return;
            }
            this.loading = true;
            try {
                await this.removeUser(item);
                await this.deleteUserProfileFirestore(item.uid);
                this.showSnack(`${item.email} has been removed from users`, 'success');
            } catch (e) {
                this.showSnack(`Oops! Failed to remove ${item.email} from users`, 'error');
                console.error(e);
            } finally {
                this.deleteDialog = false;
                this.itemToDelete = {};
                this.loading = false;
            }
        },
        async disableUser(item, value) {
            if (this.isCurrentUser(item)) {
                return;
            }
            this.loading = true;
            this.editedIndex = this.users.indexOf(item);
            this.editedItem = Object.assign({}, item);
            try {
                this.editedItem.disabled = !value;
                await this.updateUser({index: this.editedIndex, user: this.editedItem});
                this.showSnack(`${this.editedItem.email} is now ${!value ? 'inactive' : 'active'}`, 'success');
            } catch (e) {
                this.showSnack(`${this.editedItem.email} failed to be set to ${!value ? 'inactive' : 'active'}`, 'error');
                console.error(e);
            } finally {
                this.loading = false;
            }
        },
        async save() {
            const claims = [
                {
                    role: 'User',
                    value: {user: true}
                },
                {
                    role: 'Admin',
                    value: {admin: true}
                }
            ];
            if (!this.$refs.dialogForm.validate()) {
                return;
            }
            this.loading = true;
            const { displayName, photoURL } = this.editedItem;
            this.userProfileFirestore.displayName = displayName;
            this.userProfileFirestore.photoURL = photoURL || null;

            if (this.editedIndex > -1) {
                try {
                    await this.updateUser({index: this.editedIndex, user: this.editedItem});
                    if (this.editedItem.role) {
                        this.editedItem.role = claims.find(claim => claim.role === this.editedItem.role).value;
                        await this.updateUserRole({index: this.editedIndex, user: this.editedItem});
                    }
                    await this.saveUserProfileFirestore(this.editedItem.uid, this.userProfileFirestore);
                    this.showSnack(`${this.editedItem.email} has been updated`, 'success');
                } catch (e) {
                    this.showSnack(`${this.editedItem.email} couldn't be updated`, 'error');
                    console.error(e);
                } finally {
                    this.close();
                    this.loading = false;
                }
            } else {
                try {
                    if (!this.editedItem.photoURL) {
                        delete this.editedItem.photoURL
                    }
                    this.userProfileFirestore.display = false;
                    const newUserUid = await this.addUser(this.editedItem);
                    await this.saveUserProfileFirestore(newUserUid, this.userProfileFirestore);
                    this.showSnack(`${this.editedItem.email} is added to users.`, 'success');
                } catch (e) {
                    this.showSnack(`${this.editedItem.email} couldn't be added to users.`, 'error');
                    console.error(e);
                } finally {
                    this.close();
                    this.loading = false;
                }
            }
        },
        visiblePublicly(id) {
            const { display } = this.userProfileFirestore;
            firebase.firestore().collection(process.env.VUE_APP_COLLECTION_USERS)
                .doc(id)
                .update({ display })
                .then(() => {
                    this.showSnack('Account visibility updated!', 'success');
                })
                .catch(() => {
                    this.showSnack('Failed to update account visibility.', 'error');
                });
        },
        selectFile() {
            const file = this.$refs.uploader.files[0];
            if (file.size > 1000000) {
                this.showSnack('Image size should be under 1MB', 'error');
                return;
            }
            if (file && file.name) {
                this.loadingImage = true;
                const ref = firebase.storage().ref().child(`users_images/${file.name}`);
                const vm = this;
                ref.put(file).then(snapshot => {
                    if (snapshot.state === 'success') {
                        snapshot.ref.getDownloadURL().then(downloadURL => {
                            vm.editedItem.photoURL = downloadURL;
                        });
                    } else {
                        this.showSnack('Image could not be uploaded', 'error');
                    }
                    vm.loadingImage = false;
                });
            }
        }
    }
};
</script>

<style lang="scss">
#user-dialog {
    .photo-wrapper {
        position: relative;
        width: 120px;

        .remove-photo {
            position: absolute;
            z-index: 2;
            top: 0;
            right: 0;
            width: 30px;
            height: 30px;
        }
    }
}
</style>
